/**
 * DynamicComponentRenderer - Renderizador de Componentes Dinámicos
 * Permite a DamyxIA renderizar componentes React en tiempo real en el Panel Derecho
 */

import React, { useState, useEffect, useRef, Suspense, useMemo } from 'react';
import { Loader2, AlertCircle, Sparkles, Code, RefreshCw, Maximize2, X, Rainbow, Palette, Box, Sun } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';

// Colores del sistema de diseño
const COLORS = {
  background: '#0F172A',
  panel: '#1E293B',
  border: '#334155',
  textPrimary: '#FFFFFF',
  textSecondary: '#CBD5E1',
  primary: '#3B82F6',
  success: '#22C55E',
  warning: '#EAB308',
  error: '#EF4444',
  info: '#38BDF8',
};

// =====================================================
// COMPONENTES PRE-REGISTRADOS (Biblioteca de DamyxIA)
// =====================================================

// DamyxRainbow - Componente de demostración con arcoíris animado
const DamyxRainbow = ({ intensity = 'medium', animate = true }) => {
  const intensityConfig = {
    low: { blur: '20px', opacity: 0.5, speed: '8s' },
    medium: { blur: '40px', opacity: 0.7, speed: '5s' },
    high: { blur: '60px', opacity: 0.9, speed: '3s' },
  };
  
  const config = intensityConfig[intensity] || intensityConfig.medium;
  
  return (
    <div className="relative w-full h-64 rounded-2xl overflow-hidden bg-zinc-900">
      {/* Fondo con gradiente animado */}
      <div 
        className={`absolute inset-0 ${animate ? 'animate-gradient-x' : ''}`}
        style={{
          background: 'linear-gradient(90deg, #ff0080, #ff8c00, #40e0d0, #7b68ee, #ff0080)',
          backgroundSize: '300% 100%',
          filter: `blur(${config.blur})`,
          opacity: config.opacity,
          animation: animate ? `gradient-shift ${config.speed} ease infinite` : 'none',
        }}
      />
      
      {/* Contenido central */}
      <div className="relative z-10 flex flex-col items-center justify-center h-full text-white">
        <div className="flex items-center gap-3 mb-4">
          <Rainbow className="w-10 h-10" />
          <span className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-pink-500 via-purple-500 to-cyan-500">
            DamyxRainbow
          </span>
        </div>
        <p className="text-sm text-zinc-300">
          Componente renderizado dinámicamente por DamyxIA
        </p>
        <div className="mt-4 flex gap-2">
          <span className={`px-3 py-1 rounded-full text-xs ${intensity === 'low' ? 'bg-blue-500' : 'bg-zinc-700'}`}>
            Low
          </span>
          <span className={`px-3 py-1 rounded-full text-xs ${intensity === 'medium' ? 'bg-purple-500' : 'bg-zinc-700'}`}>
            Medium
          </span>
          <span className={`px-3 py-1 rounded-full text-xs ${intensity === 'high' ? 'bg-pink-500' : 'bg-zinc-700'}`}>
            High
          </span>
        </div>
      </div>
      
      {/* Estilo para animación */}
      <style jsx>{`
        @keyframes gradient-shift {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
      `}</style>
    </div>
  );
};

// DamyxSunrise - Amanecer suave y cálido
const DamyxSunrise = ({ mood = 'calm', glow = true }) => {
  const moodConfig = {
    calm: { emoji: '🌅', text: 'tranquilo y sereno' },
    energetic: { emoji: '☀️', text: 'energético y vibrante' },
    peaceful: { emoji: '🌄', text: 'pacífico y relajante' },
    hopeful: { emoji: '🌤️', text: 'esperanzador y brillante' },
  };
  
  const config = moodConfig[mood] || moodConfig.calm;
  
  return (
    <div
      className="relative w-full h-64 rounded-2xl overflow-hidden flex flex-col items-center justify-center p-10"
      style={{
        background: 'linear-gradient(to top, #0f172a, #1e293b, #f59e0b, #fbbf24)',
      }}
    >
      {/* Glow effect */}
      {glow && (
        <div 
          className="absolute bottom-0 left-1/2 -translate-x-1/2 w-48 h-48 rounded-full opacity-60"
          style={{
            background: 'radial-gradient(circle, #fbbf24 0%, #f59e0b 30%, transparent 70%)',
            filter: 'blur(30px)',
            animation: 'pulse 3s ease-in-out infinite',
          }}
        />
      )}
      
      {/* Sun circle */}
      <div 
        className="absolute bottom-4 w-24 h-12 rounded-t-full"
        style={{
          background: 'linear-gradient(to top, #f59e0b, #fbbf24, #fef3c7)',
          boxShadow: glow ? '0 0 60px 20px rgba(251, 191, 36, 0.4)' : 'none',
        }}
      />
      
      {/* Content */}
      <div className="relative z-10 text-center">
        <h1 className="text-4xl font-bold text-white mb-4 drop-shadow-lg">
          {config.emoji} DamyxSunrise
        </h1>
        <p className="text-slate-200 text-lg drop-shadow">
          Un amanecer {config.text}
        </p>
        <p className="text-amber-300 text-sm mt-2">
          mood: {mood}
        </p>
      </div>
      
      {/* Horizon line */}
      <div className="absolute bottom-16 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-400/50 to-transparent" />
      
      {/* Stars fading */}
      <div className="absolute top-4 left-8 w-1 h-1 bg-white rounded-full opacity-30" />
      <div className="absolute top-8 right-12 w-1 h-1 bg-white rounded-full opacity-20" />
      <div className="absolute top-6 left-1/3 w-1 h-1 bg-white rounded-full opacity-25" />
      
      {/* Animation styles */}
      <style jsx>{`
        @keyframes pulse {
          0%, 100% { opacity: 0.6; transform: translateX(-50%) scale(1); }
          50% { opacity: 0.8; transform: translateX(-50%) scale(1.1); }
        }
      `}</style>
    </div>
  );
};

// DamyxCard - Tarjeta estilizada
const DamyxCard = ({ title, description, icon, color = 'indigo', children }) => {
  const colorMap = {
    indigo: 'from-indigo-500 to-purple-600',
    emerald: 'from-emerald-500 to-teal-600',
    rose: 'from-rose-500 to-pink-600',
    amber: 'from-amber-500 to-orange-600',
    cyan: 'from-cyan-500 to-blue-600',
  };
  
  return (
    <div className="relative p-6 rounded-2xl bg-zinc-900 border border-zinc-800 overflow-hidden group hover:border-zinc-700 transition-all">
      {/* Glow effect */}
      <div 
        className={`absolute -top-20 -right-20 w-40 h-40 bg-gradient-to-br ${colorMap[color] || colorMap.indigo} opacity-20 blur-3xl group-hover:opacity-30 transition-opacity`} 
      />
      
      <div className="relative z-10">
        {icon && (
          <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${colorMap[color] || colorMap.indigo} flex items-center justify-center mb-4`}>
            {icon}
          </div>
        )}
        {title && (
          <h3 className="text-xl font-semibold text-white mb-2">{title}</h3>
        )}
        {description && (
          <p className="text-zinc-400 text-sm mb-4">{description}</p>
        )}
        {children}
      </div>
    </div>
  );
};

// DamyxStats - Panel de estadísticas
const DamyxStats = ({ stats = [] }) => {
  const defaultStats = [
    { label: 'Usuarios', value: '12.5K', change: '+12%', positive: true },
    { label: 'Proyectos', value: '847', change: '+23%', positive: true },
    { label: 'Conversiones', value: '94.2%', change: '-2.1%', positive: false },
    { label: 'Ingresos', value: '$48.2K', change: '+18%', positive: true },
  ];
  
  const displayStats = stats.length > 0 ? stats : defaultStats;
  
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {displayStats.map((stat, idx) => (
        <div 
          key={idx}
          className="p-4 rounded-xl bg-zinc-900 border border-zinc-800"
        >
          <p className="text-xs text-zinc-500 mb-1">{stat.label}</p>
          <p className="text-2xl font-bold text-white mb-1">{stat.value}</p>
          <span className={`text-xs ${stat.positive ? 'text-emerald-400' : 'text-red-400'}`}>
            {stat.change}
          </span>
        </div>
      ))}
    </div>
  );
};

// DamyxButton - Botón animado
const DamyxButton = ({ 
  children, 
  variant = 'primary', 
  size = 'md',
  glow = true,
  onClick 
}) => {
  const variants = {
    primary: 'bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700',
    secondary: 'bg-zinc-800 hover:bg-zinc-700 border border-zinc-700',
    success: 'bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700',
    danger: 'bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700',
  };
  
  const sizes = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-5 py-2.5 text-base',
    lg: 'px-7 py-3.5 text-lg',
  };
  
  return (
    <button 
      onClick={onClick}
      className={`
        relative rounded-xl font-medium text-white transition-all duration-300
        ${variants[variant] || variants.primary}
        ${sizes[size] || sizes.md}
        ${glow ? 'shadow-lg shadow-indigo-500/25 hover:shadow-indigo-500/40' : ''}
        active:scale-95
      `}
    >
      {children}
    </button>
  );
};

// DamyxProgress - Barra de progreso animada
const DamyxProgress = ({ value = 65, label, color = 'indigo', showValue = true }) => {
  const colorMap = {
    indigo: 'from-indigo-500 to-purple-600',
    emerald: 'from-emerald-500 to-teal-600',
    rose: 'from-rose-500 to-pink-600',
    amber: 'from-amber-500 to-orange-600',
  };
  
  return (
    <div className="w-full">
      {(label || showValue) && (
        <div className="flex justify-between mb-2">
          {label && <span className="text-sm text-zinc-400">{label}</span>}
          {showValue && <span className="text-sm text-zinc-300">{value}%</span>}
        </div>
      )}
      <div className="h-3 bg-zinc-800 rounded-full overflow-hidden">
        <div 
          className={`h-full bg-gradient-to-r ${colorMap[color] || colorMap.indigo} rounded-full transition-all duration-1000 ease-out`}
          style={{ width: `${Math.min(100, Math.max(0, value))}%` }}
        />
      </div>
    </div>
  );
};

// DamyxLoader - Indicador de carga elegante
const DamyxLoader = ({ size = 'md', text }) => {
  const sizes = {
    sm: 'w-6 h-6',
    md: 'w-10 h-10',
    lg: 'w-16 h-16',
  };
  
  return (
    <div className="flex flex-col items-center justify-center gap-4">
      <div className={`${sizes[size] || sizes.md} relative`}>
        <div className="absolute inset-0 rounded-full border-2 border-zinc-700" />
        <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-indigo-500 animate-spin" />
        <div className="absolute inset-2 rounded-full border-2 border-transparent border-t-purple-500 animate-spin" style={{ animationDirection: 'reverse', animationDuration: '0.8s' }} />
      </div>
      {text && <p className="text-sm text-zinc-400">{text}</p>}
    </div>
  );
};

// =====================================================
// REGISTRO DE COMPONENTES DISPONIBLES
// =====================================================
const COMPONENT_REGISTRY = {
  DamyxRainbow: {
    component: DamyxRainbow,
    name: 'DamyxRainbow',
    description: 'Arcoíris animado con efectos de gradiente',
    defaultProps: { intensity: 'medium', animate: true },
    icon: Rainbow,
  },
  DamyxSunrise: {
    component: DamyxSunrise,
    name: 'DamyxSunrise',
    description: 'Amanecer suave y cálido con efecto glow',
    defaultProps: { mood: 'calm', glow: true },
    icon: Sun,
  },
  DamyxCard: {
    component: DamyxCard,
    name: 'DamyxCard',
    description: 'Tarjeta estilizada con efecto glow',
    defaultProps: { title: 'Título', description: 'Descripción', color: 'indigo' },
    icon: Box,
  },
  DamyxStats: {
    component: DamyxStats,
    name: 'DamyxStats',
    description: 'Panel de estadísticas',
    defaultProps: { stats: [] },
    icon: Sparkles,
  },
  DamyxButton: {
    component: DamyxButton,
    name: 'DamyxButton',
    description: 'Botón animado con variantes',
    defaultProps: { children: 'Click me', variant: 'primary' },
    icon: Box,
  },
  DamyxProgress: {
    component: DamyxProgress,
    name: 'DamyxProgress',
    description: 'Barra de progreso animada',
    defaultProps: { value: 65, label: 'Progreso' },
    icon: Palette,
  },
  DamyxLoader: {
    component: DamyxLoader,
    name: 'DamyxLoader',
    description: 'Indicador de carga elegante',
    defaultProps: { size: 'md', text: 'Cargando...' },
    icon: Loader2,
  },
};

// =====================================================
// COMPONENTE PRINCIPAL: DynamicComponentRenderer
// =====================================================
export const DynamicComponentRenderer = ({ 
  componentData, 
  onClose,
  showControls = true 
}) => {
  const { language } = useLanguage();
  const [error, setError] = useState(null);
  const [propsOverride, setPropsOverride] = useState({});
  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = useRef(null);
  
  // Parsear los datos del componente
  const parsedData = useMemo(() => {
    if (!componentData) return null;
    
    // Si es string, intentar parsear como JSON
    if (typeof componentData === 'string') {
      try {
        return JSON.parse(componentData);
      } catch {
        return null;
      }
    }
    
    return componentData;
  }, [componentData]);
  
  // Obtener el componente del registro
  const registeredComponent = useMemo(() => {
    if (!parsedData?.component) return null;
    return COMPONENT_REGISTRY[parsedData.component];
  }, [parsedData]);
  
  // Props finales (defaultProps + props enviados + overrides)
  const finalProps = useMemo(() => {
    if (!registeredComponent) return {};
    return {
      ...registeredComponent.defaultProps,
      ...(parsedData?.props || {}),
      ...propsOverride,
    };
  }, [registeredComponent, parsedData, propsOverride]);
  
  // Renderizar el componente
  const renderComponent = () => {
    if (!parsedData) {
      return (
        <div className="flex flex-col items-center justify-center h-full text-zinc-400">
          <AlertCircle className="w-12 h-12 mb-4 text-zinc-600" />
          <p>{language === 'es' ? 'No hay datos de componente' : 'No component data'}</p>
        </div>
      );
    }
    
    if (!registeredComponent) {
      return (
        <div className="flex flex-col items-center justify-center h-full text-zinc-400">
          <AlertCircle className="w-12 h-12 mb-4 text-amber-500" />
          <p className="text-lg font-medium text-amber-400 mb-2">
            {language === 'es' ? 'Componente no encontrado' : 'Component not found'}
          </p>
          <p className="text-sm text-zinc-500">
            "{parsedData.component}" {language === 'es' ? 'no está registrado' : 'is not registered'}
          </p>
          <div className="mt-4 text-xs text-zinc-600">
            <p className="mb-2">{language === 'es' ? 'Componentes disponibles:' : 'Available components:'}</p>
            <div className="flex flex-wrap gap-2 justify-center">
              {Object.keys(COMPONENT_REGISTRY).map(name => (
                <span key={name} className="px-2 py-1 bg-zinc-800 rounded text-zinc-400">
                  {name}
                </span>
              ))}
            </div>
          </div>
        </div>
      );
    }
    
    try {
      const Component = registeredComponent.component;
      return <Component {...finalProps} />;
    } catch (err) {
      setError(err.message);
      return (
        <div className="flex flex-col items-center justify-center h-full text-red-400">
          <AlertCircle className="w-12 h-12 mb-4" />
          <p className="text-lg font-medium mb-2">
            {language === 'es' ? 'Error al renderizar' : 'Render error'}
          </p>
          <p className="text-sm text-zinc-500">{err.message}</p>
        </div>
      );
    }
  };
  
  return (
    <div 
      ref={containerRef}
      className={`
        relative flex flex-col bg-zinc-950 rounded-xl overflow-hidden border border-zinc-800
        ${isFullscreen ? 'fixed inset-4 z-50' : 'h-full'}
      `}
    >
      {/* Header */}
      {showControls && (
        <div className="flex items-center justify-between px-4 py-3 bg-zinc-900 border-b border-zinc-800">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
              {registeredComponent?.icon ? (
                <registeredComponent.icon className="w-4 h-4 text-white" />
              ) : (
                <Code className="w-4 h-4 text-white" />
              )}
            </div>
            <div>
              <h3 className="text-sm font-medium text-white">
                {parsedData?.component || 'Component'}
              </h3>
              <p className="text-xs text-zinc-500">
                {registeredComponent?.description || (language === 'es' ? 'Componente dinámico' : 'Dynamic component')}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPropsOverride({})}
              className="p-2 rounded-lg hover:bg-zinc-800 text-zinc-400 hover:text-white transition-colors"
              title={language === 'es' ? 'Reiniciar' : 'Reset'}
            >
              <RefreshCw className="w-4 h-4" />
            </button>
            <button
              onClick={() => setIsFullscreen(!isFullscreen)}
              className="p-2 rounded-lg hover:bg-zinc-800 text-zinc-400 hover:text-white transition-colors"
              title={isFullscreen ? (language === 'es' ? 'Minimizar' : 'Minimize') : (language === 'es' ? 'Maximizar' : 'Maximize')}
            >
              <Maximize2 className="w-4 h-4" />
            </button>
            {onClose && (
              <button
                onClick={onClose}
                className="p-2 rounded-lg hover:bg-zinc-800 text-zinc-400 hover:text-white transition-colors"
                title={language === 'es' ? 'Cerrar' : 'Close'}
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      )}
      
      {/* Content Area */}
      <div className="flex-1 p-6 overflow-auto">
        <Suspense fallback={<DamyxLoader text={language === 'es' ? 'Cargando componente...' : 'Loading component...'} />}>
          {renderComponent()}
        </Suspense>
      </div>
      
      {/* Footer con props actuales */}
      {showControls && parsedData && (
        <div className="px-4 py-2 bg-zinc-900/50 border-t border-zinc-800">
          <div className="flex items-center justify-between">
            <span className="text-xs text-zinc-500">
              {language === 'es' ? 'Props:' : 'Props:'}
            </span>
            <code className="text-xs text-indigo-400 font-mono max-w-md truncate">
              {JSON.stringify(finalProps)}
            </code>
          </div>
        </div>
      )}
      
      {/* Fullscreen overlay backdrop */}
      {isFullscreen && (
        <div 
          className="fixed inset-0 bg-black/80 -z-10"
          onClick={() => setIsFullscreen(false)}
        />
      )}
    </div>
  );
};

// =====================================================
// EXPORTAR COMPONENTES Y REGISTRO
// =====================================================
export { 
  COMPONENT_REGISTRY,
  DamyxRainbow,
  DamyxSunrise,
  DamyxCard,
  DamyxStats,
  DamyxButton,
  DamyxProgress,
  DamyxLoader,
};

export default DynamicComponentRenderer;
